import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowRight, CheckCircle, Brain, Heart, Shield, Zap } from 'lucide-react';
import CTA from '../components/CTA';
import FreeGuideCTA from '../components/FreeGuideCTA';
import PillarClusterNav from '../components/PillarClusterNav';
import { fetchBlogPostsByNiche, BlogPost } from '../utils/blogPosts';

const StressPhysicalSymptoms: React.FC = () => {
  const [clusterBlogs, setClusterBlogs] = useState<BlogPost[]>([]);
  const [blogsLoading, setBlogsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const posts = await fetchBlogPostsByNiche('stress-symptoms');
      setClusterBlogs(posts);
      setBlogsLoading(false);
    };
    load();
  }, []);

  const stressServicePages = [
    { href: '/jaw-tension-hypnotherapy', label: 'Jaw Tension & Bruxism' },
    { href: '/bladder-urgency-hypnotherapy', label: 'Bladder Urgency / OAB' },
    { href: '/ibs-hypnotherapy', label: 'IBS & Gut Symptoms' },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Can stress really cause physical symptoms?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Chronic stress activates the fight-or-flight response, which can manifest as jaw tension, IBS, bladder urgency, headaches, and other physical symptoms. The body is not broken — it is responding to sustained nervous system activation.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does hypnotherapy help with stress-related physical symptoms?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Hypnotherapy works with the subconscious mind to calm the overactive stress response. By addressing the root cause rather than just the symptom, it helps your nervous system learn to regulate itself again.',
        },
      },
      {
        '@type': 'Question',
        name: 'How many sessions will I need?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most clients notice meaningful improvement within 3-6 sessions, though this varies depending on how long the pattern has been established and the specific symptoms involved.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can hypnotherapy help if my doctor says nothing is physically wrong?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely. When medical tests come back clear but symptoms persist, it often points to a stress-conditioned pattern. Hypnotherapy is specifically designed to work with these mind-body connections.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I need a referral from my GP?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No referral is needed. However, it is always advisable to have your symptoms checked by your GP first to rule out any underlying medical conditions.',
        },
      },
    ],
  };

  const faqs = [
    {
      question: 'Can stress really cause physical symptoms?',
      answer: 'Yes. Chronic stress activates the fight-or-flight response, which can manifest as jaw tension, IBS, bladder urgency, headaches, and other physical symptoms. The body is not broken — it is responding to sustained nervous system activation.',
    },
    {
      question: 'How does hypnotherapy help with stress-related physical symptoms?',
      answer: 'Hypnotherapy works with the subconscious mind to calm the overactive stress response. By addressing the root cause rather than just the symptom, it helps your nervous system learn to regulate itself again.',
    },
    {
      question: 'How many sessions will I need?',
      answer: 'Most clients notice meaningful improvement within 3-6 sessions, though this varies depending on how long the pattern has been established and the specific symptoms involved.',
    },
    {
      question: 'Can hypnotherapy help if my doctor says nothing is physically wrong?',
      answer: 'Absolutely. When medical tests come back clear but symptoms persist, it often points to a stress-conditioned pattern. Hypnotherapy is specifically designed to work with these mind-body connections.',
    },
    {
      question: 'Do I need a referral from my GP?',
      answer: 'No referral is needed. However, it is always advisable to have your symptoms checked by your GP first to rule out any underlying medical conditions.',
    },
  ];

  return (
    <main>
      <Helmet>
        <title>Stress-Related Physical Symptoms | Hypnotherapy | Sharon Clare</title>
        <meta
          name="description"
          content="When stress lives in your body as jaw tension, IBS, bladder urgency, or other physical symptoms, hypnotherapy can help. Sharon Clare offers clinical hypnotherapy in Belfast and across Northern Ireland."
        />
        <meta
          name="keywords"
          content="stress physical symptoms hypnotherapy, mind body hypnotherapy Belfast, stress-conditioned symptoms Northern Ireland"
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/stress-physical-symptoms/" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">
            When Stress Lives in Your Body
          </h1>
          <p className="text-xl font-heading text-gray-700 mb-6">
            Your body isn't broken — it's responding to stress. Hypnotherapy can help you find lasting relief from physical symptoms driven by your nervous system.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/book"
              className="btn-primary inline-flex items-center justify-center"
            >
              <Calendar className="w-5 h-5 mr-2" /> Request a 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" /> Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Explanatory section */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
            The Stress Response Is Driving Your Symptoms
          </h2>
          <div className="prose prose-lg text-gray-700 leading-relaxed space-y-5">
            <p>
              If you've been told there's nothing physically wrong, but you're still experiencing jaw tension, digestive upset, bladder urgency, tension headaches, or broken sleep — your body isn't lying. It's caught in an overactive stress response, and the symptoms are real.
            </p>
            <p>
              When we experience prolonged stress or anxiety, the autonomic nervous system shifts into a state of sustained alertness. The body prepares for threat — muscles tighten, digestion slows, bladder sensitivity increases, the jaw clenches. In short bursts, this is healthy and protective. But when the stress response stays switched on, these physical changes become habitual. The body learns to hold tension in ways that eventually cause pain, dysfunction, and distress.
            </p>
            <p>
              Common stress-conditioned physical symptoms include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Jaw tension and bruxism</strong> — clenching or grinding driven by the nervous system's habit of holding stress in the jaw</li>
              <li><strong>IBS and gut symptoms</strong> — the gut-brain axis means the digestive system is highly sensitive to the stress response</li>
              <li><strong>Bladder urgency and OAB</strong> — the bladder can become hypersensitive when the nervous system is on high alert</li>
              <li><strong>Tension headaches and migraines</strong> — often triggered by habitual muscle tension and nervous system overactivation</li>
              <li><strong>Sleep disruption</strong> — the stress response and sleep regulation share the same neural pathways</li>
            </ul>
            <p>
              The reason these symptoms persist, even after you've "tried everything," is that they're not driven by a structural problem — they're driven by a subconscious pattern. Physical treatments, medication, and lifestyle changes can provide relief, but they rarely address the underlying nervous system conditioning that's maintaining the symptom. That's where hypnotherapy works differently.
            </p>
            <p>
              Clinical hypnotherapy works directly with the subconscious mind — the part that runs the stress response automatically, below the level of conscious thought. In a calm, focused state, the mind becomes more receptive to new patterns. Rather than managing the symptom, hypnotherapy helps your nervous system learn to regulate itself again: releasing the habitual tension, calming the overactive response, and restoring a more settled baseline.
            </p>
            <p>
              Sharon Clare is a clinical hypnotherapist working in Belfast and across Northern Ireland, with online sessions also available. Her approach is warm, professional, and grounded in the understanding that your symptoms make sense — and there is another way.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {[
              { icon: <Brain className="w-6 h-6 text-accent-600" />, title: 'Address the root cause' },
              { icon: <Shield className="w-6 h-6 text-accent-600" />, title: 'Calm the stress response' },
              { icon: <Heart className="w-6 h-6 text-accent-600" />, title: 'Restore nervous system balance' },
              { icon: <Zap className="w-6 h-6 text-accent-600" />, title: 'Release habitual tension patterns' },
            ].map(({ icon, title }) => (
              <div key={title} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center space-x-3">
                  {icon}
                  <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PillarClusterNav */}
      <PillarClusterNav
        servicePages={stressServicePages}
        clusterBlogs={blogsLoading ? [] : clusterBlogs.map(b => ({ slug: b.slug, title: b.title }))}
      />

      {/* Free Guide Lead Magnet */}
      <FreeGuideCTA location="Stress Pillar" />

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map(({ question, answer }) => (
              <div key={question} className="border border-gray-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-2">{question}</h3>
                    <p className="text-gray-700 leading-relaxed">{answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Ready to Take the Next Step?"
        subtitle="Book a free 10-minute call to discuss how hypnotherapy can help."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default StressPhysicalSymptoms;
